import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { AiIcon } from '../../components/AiIcon';
import { Specialist } from '../../Utils/specialists';
import {
  Dropdown,
  DropdownGroup,
  DropdownItem,
  DropdownLabel,
  DropdownSearch,
  PickerWrapper,
  PillButton,
  ViewAllButton
} from './styles';

const DROPDOWN_OFFSET = 4;

export interface SpecialistPickerProps {
  options: Specialist[];
  selected?: Specialist;
  onSelect: (specialist: Specialist) => void;
  onViewAll?: () => void;
}

export const SpecialistPicker: FC<SpecialistPickerProps> = ({
  options,
  selected,
  onSelect,
  onViewAll
}) => {
  const { t } = useTranslation('activities');
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ left: 0, bottom: 0 });

  // Renderizado em portal (posição fixa) para não ser cortado pelo painel do chat
  useLayoutEffect(() => {
    if (!isOpen || !wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    setPosition({
      left: rect.left,
      bottom: window.innerHeight - rect.top + DROPDOWN_OFFSET
    });
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !wrapperRef.current?.contains(target) &&
        !dropdownRef.current?.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) setSearch('');
  }, [isOpen]);

  const filtered = options.filter((option) =>
    t(option.labelKey)
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  return (
    <PickerWrapper ref={wrapperRef}>
      {isOpen &&
        createPortal(
          <Dropdown ref={dropdownRef} role="listbox" style={position}>
            <DropdownSearch>
              <AiIcon name="search" />
              <input
                autoFocus
                value={search}
                placeholder={t('aiChat.searchSpecialist')}
                onChange={(event) => setSearch(event.target.value)}
              />
            </DropdownSearch>
            <DropdownGroup>
              <DropdownLabel>{t('aiChat.specialists')}</DropdownLabel>
              {filtered.map((option) => (
                <DropdownItem
                  key={option.id}
                  type="button"
                  role="option"
                  aria-selected={option.id === selected?.id}
                  $active={option.id === selected?.id}
                  onClick={() => {
                    onSelect(option);
                    setIsOpen(false);
                  }}
                >
                  <img src={option.image} alt="" />
                  <span>{t(option.labelKey)}</span>
                </DropdownItem>
              ))}
            </DropdownGroup>
            {onViewAll && (
              <ViewAllButton
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  onViewAll();
                }}
              >
                {t('aiChat.viewAll')}
              </ViewAllButton>
            )}
          </Dropdown>,
          document.body
        )}
      <PillButton
        type="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
      >
        {selected ? (
          <img className="persona" src={selected.image} alt="" />
        ) : (
          <AiIcon name="specialists" />
        )}
        <span>{selected ? t(selected.labelKey) : t('aiChat.specialists')}</span>
        <span className="chevron">
          <AiIcon name="chevronDown" box={14} />
        </span>
      </PillButton>
    </PickerWrapper>
  );
};
